import { Alpine } from '../../core/alpine-core.js';
import { apiFetch } from '../api.js';
import { CURRENT_SEASON } from '../../core/current-season.js';

Alpine.data('teamsList', () => ({

    items: [],
    loading: true,
    refreshSpinning: false,

    /** League keys from `/dashboard-layout/meta` (admin); empty = only "All leagues" in UI. */
    metaLeagueKeys: [],

    page: 1, totalPages: 1, total: 0,
    /** Omit `league` query param when empty so all leagues for this account are listed. */
    filters: { q: '', active: true, league: '', season: CURRENT_SEASON, sortBy: 'name:asc', limit: 20 },

   
    mode: 'add',
    currentTeamId: null,
    addType: 'roster',

    // Stats editor modal
    statsOpen: false,
    statsForm: { league: 'PDL', season: CURRENT_SEASON, played: 0, won: 0, drawn: 0, lost: 0, legsFor: 0, legsAgainst: 0, points: 0 },

    placeholderLogo: '/assets/placeholder-team.png',

    rosterOpen: false,
    currentTeam: null,
    roster: [], loans: [],
    playerSearch: '',
    searchResults: [],
    selectedToAdd: [],

    filtersOpen: false,
    firstLoad: true,

    scrollToTopAfterPageChange() {
        if (window.innerWidth >= 768) return;
        window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    async init() {
        await this.loadMetaLeagueKeys();
        await this.reload();
        this.firstLoad = false;
    },

    async loadMetaLeagueKeys() {
        try {
            const res = await apiFetch('/dashboard-layout/meta');
            const j = await res.json().catch(() => ({}));
            if (!res.ok) return;
            const opts = Array.isArray(j?.leagueOptions) ? j.leagueOptions : [];
            this.metaLeagueKeys = [...new Set(
                opts.map((o) => String(o?.leagueKey || '').trim().toUpperCase()).filter(Boolean),
            )];
        } catch {
            /* ignore */
        }
    },

    // helpers
    idOf(v) { return typeof v === 'string' ? v : (v?._id || ''); },
    displayCaptain(t) {
        const p = t?.captainPlayerId;
        if (!p) return '—';
        if (p.fullName) return p.fullName; // p.nickname ? `${p.fullName} (${p.nickname})` : p.fullName;
        return '—';
    },

    _qs() {
        const p = new URLSearchParams({
            q: this.filters.q || '',
            active: this.filters.active || '',
            sortBy: this.filters.sortBy,
            page: String(this.page),
            limit: String(this.filters.limit)
        });
        const league = String(this.filters.league || '').trim();
        if (league) p.set('league', league);
        if (this.filters.season) p.set('season', this.filters.season);
        return p.toString();
    },

    async reload() {
        this.loading = true;
        try {
            const res = await apiFetch(`/teams?${this._qs()}`);
            const data = await res.json();
            this.items = data.items || [];
            this.page = data.page || 1;
            this.totalPages = data.totalPages || 1;
            this.total = data.totalResults ?? data.total ?? 0;
        } finally {
            this.loading = false;
            this.refreshSpinning = false;
        }
    },

    async manualRefresh() {
        this.refreshSpinning = true;
        await this.reload();
    },

    showPager() {
        // hide pager when 0 or 1 page
        return Number(this.totalPages || 0) > 1;
    },
    from() {
        if (!this.total) return 0;
        const limit = Number(this.limit ?? this.filters?.limit ?? 20);
        return (this.page - 1) * limit + 1;
    },
    to() {
        if (!this.total) return 0;
        const limit = Number(this.limit ?? this.filters?.limit ?? 20);
        const end = this.page * limit;
        return end > this.total ? this.total : end;
    },

    hasActiveFilters() {
        const f = this.filters || {};
        return !!(
            (f.q && f.q.trim()) ||
            (f.sortBy && f.sortBy !== 'name:asc') ||
            (Number(f.limit) !== 20) ||
            String(f.league || '').trim()
        );
    },

    clearListFilters() {
        this.filters.q = '';
        this.filters.sortBy = 'name:asc';
        this.filters.limit = 20;
        this.filters.league = '';
        this.page = 1;
        return this.reload();
    },

    async go(p) {
        const n = Math.min(Math.max(1, p), this.totalPages || 1);
        if (n === this.page) return;
        this.page = n;
        await this.reload();
        this.scrollToTopAfterPageChange();
    },

    // Stats editor
    openStats(t) {
        this.currentTeam = t;
        const base = (t.statsCurrent || null);
        this.statsForm = base ? { ...base } : { league: String(this.filters.league || '').trim() || 'PDL', season: this.filters.season || CURRENT_SEASON, played: 0, won: 0, drawn: 0, lost: 0, legsFor: 0, legsAgainst: 0, points: 0 };
        this.statsOpen = true;
    },
    async saveStats() {
        const r = await apiFetch(`/teams/${this.currentTeam._id}/stats`, { method: 'PATCH', body: this.statsForm });
        if (!r.ok) return alert('Save failed');
        this.statsOpen = false;
        await this.reload();
    },

    async openRoster(t) {
        this.currentTeam = t;
        const r = await apiFetch(`/teams/${t._id}/roster`);
        const data = await r.json();
        this.roster = data.roster || [];
        this.reserves = data.reserves || [];
        this.rosterOpen = true;
        this.searchResults = [];
        this.selectedToAdd = [];
        this.addType = 'roster';
        this.playerSearch = '';
    },

    async searchPlayersForRoster() {
        const q = String(this.playerSearch || '').trim();
        if (!q) {
            this.searchResults = [];
            return;
        }
        try {
            const res = await apiFetch(`/players?q=${encodeURIComponent(q)}&limit=30`);
            const data = await res.json().catch(() => ({}));
            this.searchResults = Array.isArray(data?.items) ? data.items : [];
        } catch {
            this.searchResults = [];
        }
    },

    toggleSelectPlayer(pid) {
        const id = String(pid || '');
        if (!id) return;
        if (this.selectedToAdd.includes(id)) {
            this.selectedToAdd = this.selectedToAdd.filter((x) => x !== id);
        } else {
            this.selectedToAdd = [...this.selectedToAdd, id];
        }
    },

    async addSelected() {
        if (!this.currentTeam || !this.selectedToAdd.length) return;
        const body = { type: this.addType, playerId: '' };
        for (const pid of this.selectedToAdd) {
            body.playerId = pid;
            await apiFetch(`/teams/${this.currentTeam._id}/roster`, { method: 'POST', body });
        }
        await this.openRoster(this.currentTeam);
        this.selectedToAdd = [];
    },

    async removeFrom(pid, type) {
        await apiFetch(`/teams/${this.currentTeam._id}/roster`, { method: 'DELETE', body: { playerId: pid, type } });
        await this.openRoster(this.currentTeam);
    }

}));
